import type { TFunction } from 'i18next';
import type {
  AssistantFailureReason,
  AssistantMessage,
  ConfirmationPresenter,
} from '@/stores/assistantStore';

/**
 * The text an assistant turn shows (#2199, #2587).
 *
 * The harness sends `content` in English (the tool's message, the plan's
 * summary) because that is what the model and the logs read. What a user
 * reads is rendered here from the `assistant` bundle:
 *
 *  - a preview REFUSAL (`type: 'message'` with `failure`) → the reason,
 *    `failureReasons.<code>`;
 *  - a settled turn with the card's `presenter` → `confirmation.settled.<ok|
 *    unknown|cancelled>` around `confirmation.actions.<presenter>`, the same
 *    action the card asked about, phrased as a result;
 *  - a `failed` turn with `failure` → `confirmation.outcome.failed` around the
 *    localised reason;
 *  - a cancel marked `outcome: 'unknown'` (#2597) → the uncertainty line.
 *
 * Every lookup that misses — an unknown or new code, a presenter without an
 * action phrase, a placeholder the params did not fill — falls back to a
 * GENERIC localised line of the same kind, never to a raw key and never to
 * the English `content`. A turn WITHOUT the new fields (a row written before
 * #2587, a plain message) renders exactly as before: `content`, framed by
 * `confirmation.outcome.*` for a settled confirmation.
 *
 * `replace` carries the values so a string that happens to look like an
 * i18next option cannot steer the lookup.
 */

const UNFILLED_PLACEHOLDER = /\{\{\s*[\w.]+\s*\}\}/;
/** Returned by `t` for a missing key; never a string a bundle would hold. */
const MISSING = '\u0000missing';
/** A reason code is a flat wire identifier; anything else never reaches `t`. */
const REASON_CODE = /^[a-z][a-z0-9_]*$/;
const PRESENTER_PREFIX = 'confirmation.presenters.';
// Existing names plus exactly the reviewed lifecycle presenters; no arbitrary suffix widening.
const PRESENTER_SUFFIX = /^(?:[A-Za-z0-9]+|(?:start|pause|resume)_work_order_work)$/;

type AssistantT = TFunction<'assistant'>;

/** `t(key, params)`, or `undefined` when the key is missing or a placeholder stayed unfilled. */
function lookup(t: AssistantT, key: string, params: Record<string, unknown> = {}): string | undefined {
  const rendered: unknown = t(key, { replace: params, defaultValue: MISSING, returnObjects: true });
  if (typeof rendered !== 'string' || rendered === MISSING) return undefined;
  if (UNFILLED_PLACEHOLDER.test(rendered)) return undefined;
  return rendered;
}

/**
 * A generic line of a structured turn. In the bundle by contract; the raw
 * English `content` is NEVER the fallback here (a structured turn carries the
 * model's English, which is exactly what #2587 keeps off the screen). Should
 * the bundle itself be missing, the assistant's own error line is shown.
 */
function generic(t: AssistantT, key: string): string {
  return lookup(t, key) ?? lookup(t, 'errorMessage') ?? '';
}

/** The localised reason, or `undefined` for a code this bundle does not know. */
export function failureReasonText(t: AssistantT, failure: AssistantFailureReason): string | undefined {
  if (!REASON_CODE.test(failure.code)) return undefined;
  return lookup(t, `failureReasons.${failure.code}`, failure.params);
}

/** The card's action phrased as a result, or `undefined` when the bundle has no phrase for it. */
export function presenterActionText(t: AssistantT, presenter: ConfirmationPresenter): string | undefined {
  if (!presenter.key.startsWith(PRESENTER_PREFIX)) return undefined;
  const suffix = presenter.key.slice(PRESENTER_PREFIX.length);
  if (!PRESENTER_SUFFIX.test(suffix)) return undefined;
  return lookup(t, `confirmation.actions.${suffix}`, presenter.params);
}

/** Pre-#2587 rendering, unchanged: the outcome frame around `content`. */
function legacySettled(t: AssistantT, message: AssistantMessage): string {
  if (message.type !== 'action_confirmed' || !message.outcome) return message.content;
  const rendered: unknown = t(`confirmation.outcome.${message.outcome}`, {
    replace: message.outcome === 'failed' ? { detail: message.content } : { action: message.content },
    defaultValue: message.content,
    returnObjects: true,
  });
  if (typeof rendered !== 'string') return message.content;
  if (UNFILLED_PLACEHOLDER.test(rendered)) return message.content;
  return rendered;
}

/**
 * Whether a turn is one of the portal's own structured lines — a settled
 * confirmation or a coded refusal — rather than model prose (#2587).
 *
 * A structured line is rendered as PLAIN TEXT, never as Markdown: its params
 * are data (a customer name, a line description), and a name such as
 * `[Kirjaudu](https://example.test/login)` or `![x](https://…)` must not
 * become a link or an image. Plain text is chosen over escaping each param
 * because GFM also autolinks a bare `https://…`, which has no metacharacter
 * to escape, and because no Markdown was ever meant in these lines.
 */
export function isStructuredTurn(message: AssistantMessage): boolean {
  if (message.type === 'action_confirmed' || message.type === 'action_cancelled') return true;
  return message.failure !== undefined && (message.type === undefined || message.type === 'message');
}

export function assistantTurnText(t: AssistantT, message: AssistantMessage): string {
  if (message.type === 'action_cancelled') {
    if (message.outcome === 'unknown') return generic(t, 'confirmation.settled.cancelledUncertain');
    // A row written before #2587: its content is the handler's own text.
    if (!message.presenter) return message.content;
    const action = presenterActionText(t, message.presenter);
    return action
      ? (lookup(t, 'confirmation.settled.cancelled', { action }) ?? generic(t, 'confirmation.settled.cancelledGeneric'))
      : generic(t, 'confirmation.settled.cancelledGeneric');
  }

  if (message.type === 'action_confirmed') {
    // The legacy rendering (the outcome frame around `content`) is ONLY for a
    // row with none of the #2587 metadata. A partially structured turn — a
    // presenter without a failure code, a failure without a usable outcome —
    // carries the model's English in `content`, so it gets the generic line
    // of its kind instead (#2587 review).
    const structured = message.presenter !== undefined || message.failure !== undefined;
    if (!structured) return legacySettled(t, message);
    // The portal normalises a present-but-invalid outcome to `unknown`; this
    // covers a message built without it.
    const outcome = message.outcome ?? 'unknown';
    if (outcome === 'failed') {
      const reason = message.failure ? failureReasonText(t, message.failure) : undefined;
      return reason
        ? (lookup(t, 'confirmation.outcome.failed', { detail: reason }) ?? generic(t, 'confirmation.settled.failedGeneric'))
        : generic(t, 'confirmation.settled.failedGeneric');
    }
    const action = message.presenter ? presenterActionText(t, message.presenter) : undefined;
    const frame = `confirmation.settled.${outcome}`;
    const fallback = `confirmation.settled.${outcome}Generic`;
    return action ? (lookup(t, frame, { action }) ?? generic(t, fallback)) : generic(t, fallback);
  }

  // A preview refusal. Any other turn (a plain answer, a pending card's
  // fallback text) is shown as it came.
  if (message.failure && (message.type === undefined || message.type === 'message')) {
    return failureReasonText(t, message.failure) ?? generic(t, 'confirmation.settled.refusedGeneric');
  }
  return legacySettled(t, message);
}
